import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Paper,
  Divider,
  useMediaQuery,
  CircularProgress,
  Alert,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  ListItem,
  ListItemIcon
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AuthContext } from '../../context/AuthContext';
import FormRenderer from './FormRenderer';
import React, { useState, useEffect, useContext } from 'react';
import { ChevronRight, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { API_URL } from '../../utils/apiConfig';
import BenefitsResources from './BenefitsResources';

export default function EmployeeForms() {
  const { employeeId } = useContext(AuthContext);
  const [formsList, setFormsList] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Swipe functionality disabled - too sensitive
  // const [touchStart, setTouchStart] = useState(null);
  // const [touchEnd, setTouchEnd] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHideNavigation } = useNavigation();

  // Swipe functionality disabled - too sensitive
  // Minimum swipe distance (in px)
  // const minSwipeDistance = 50;
  
  // Clean up - we're not hiding navigation anymore
  useEffect(() => {
    return () => {
      setHideNavigation(false);
    };
  }, [setHideNavigation]);

  // Swipe functionality disabled - too sensitive
  // const onTouchStart = (e) => {
  //   setTouchEnd(null);
  //   setTouchStart(e.targetTouches[0].clientX);
  // };

  // const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  // const onTouchEnd = () => {
  //   if (!touchStart || !touchEnd) return;
    
  //   const distance = touchStart - touchEnd;
  //   const isLeftSwipe = distance > minSwipeDistance;
  //   const isRightSwipe = distance < -minSwipeDistance;
    
  //   if (activeForm && formsList.length > 0) {
  //     const currentIndex = formsList.findIndex(f => f.formId === activeForm);
      
  //     if (isLeftSwipe && currentIndex < formsList.length - 1) {
  //       // Go to next form
  //       setActiveForm(formsList[currentIndex + 1].formId);
  //     } else if (isRightSwipe && currentIndex > 0) {
  //       // Go to previous form
  //       setActiveForm(formsList[currentIndex - 1].formId);
  //     }
  //   }
  // };

  
  const updateFormStatus = async (formId, isCompleted) => {
    // Immediately update the UI
    setFormsList(prevForms =>
      prevForms.map(form =>
        form.formId === formId ? { ...form, isCompleted } : form
      )
    );
  
    try {
      // Fetch the latest forms data from the server to ensure we're in sync
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employee_forms`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch updated forms');
      }
  
      const data = await response.json();
      setFormsList(data);
    } catch (err) {
      console.error('Error updating forms list:', err);
      // Optionally show an error message to the user
      setError('Failed to update form status');
      // Revert the optimistic update if the server request failed
      setFormsList(prevForms =>
        prevForms.map(form =>
          form.formId === formId ? { ...form, isCompleted: !isCompleted } : form
        )
      );
    }
  };

  useEffect(() => {
    async function fetchEmployeeForms() {
      if (!employeeId) return; // Ensure employeeId is available
  
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching forms with employeeId:', employeeId);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/employee_forms`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          },
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch forms');
        }
  
        const data = await response.json();
        console.log('Forms fetched:', data);
        setFormsList(data);
      } catch (err) {
        console.error('Error fetching forms:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  
    fetchEmployeeForms();
  }, [employeeId]); // 🔥 Employee ID changes trigger a re-fetch
  


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }
  

  if (formsList.length === 0) {
    return (
      <Typography sx={{ p: 2 }} color="text.secondary">
        No forms available.
      </Typography>
    );
  }

  // Mobile layout
  if (isMobile) {
    const activeFormDetails = formsList.find(f => f.formId === activeForm);
    const currentFormIndex = formsList.findIndex(f => f.formId === activeForm);
    
    // Adjust height based on reduced navigation
    const boxHeight = 'calc(100vh - 64px)'; // Accounts for compact nav (48px) + padding
    
    return (
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: boxHeight,
          width: '100%',
          position: 'relative',
          bgcolor: '#f5f5f5'
        }}
      >
        {/* iOS Style Navigation Bar - Compact for mobile */}
        <AppBar 
          position="sticky" 
          sx={{ 
            bgcolor: '#f8f8f8',
            color: 'text.primary',
            boxShadow: 'none',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Toolbar sx={{ minHeight: 44 }}>
            {activeForm ? (
              <>
                <IconButton
                  edge="start"
                  onClick={() => setActiveForm(null)}
                  sx={{ mr: 1, p: 0.5, color: '#007AFF' }}
                >
                  <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="body1" sx={{ flexGrow: 1, textAlign: 'center', pr: 3, fontSize: '0.9rem', fontWeight: 500 }}>
                  {activeFormDetails?.formName}
                </Typography>
              </>
            ) : (
              <Typography variant="body1" sx={{ flexGrow: 1, textAlign: 'center', fontSize: '0.9rem', fontWeight: 500 }}>
                Employee Forms
              </Typography>
            )}
          </Toolbar>
        </AppBar>

        {/* Main Content Area */}
        {!activeForm ? (
          // iOS Style List View
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: '#f5f5f5'
          }}>
            <List sx={{ 
              bgcolor: 'white',
              m: 2,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              {formsList.map((form, index) => (
                <React.Fragment key={form.formId}>
                  <ListItem
                    button
                    onClick={() => setActiveForm(form.formId)}
                    sx={{
                      py: 2,
                      px: 2,
                      '&:hover': {
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {form.isCompleted ? (
                        <Check size={24} color="#4caf50" />
                      ) : (
                        <AlertCircle size={24} color="#ff9800" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={form.formName}
                      secondary={form.isCompleted ? "Completed" : "Not completed"}
                      primaryTypographyProps={{
                        sx: { fontWeight: 500 }
                      }}
                    />
                    <ChevronRight size={20} color="#c7c7cc" />
                  </ListItem>
                  {index < formsList.length - 1 && (
                    <Divider component="li" sx={{ ml: 7 }} />
                  )}
                </React.Fragment>
              ))}
            </List>

            {/* Company benefits documents — reference material, not forms.
                Renders nothing when the employee's store has none published. */}
            <BenefitsResources />

            {/* Summary Card */}
            <Box sx={{ mx: 2, mb: 2 }}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Forms Summary
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="h6" color="success.main">
                      {formsList.filter(f => f.isCompleted).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Typography variant="h6" color="warning.main">
                      {formsList.filter(f => !f.isCompleted).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remaining
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        ) : (
          // Active Form View
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#f5f5f5',
            height: '100%'
          }}>
            {/* Navigation Controls - Compact */}
            <Box sx={{ 
              bgcolor: 'white',
              borderBottom: '1px solid #e0e0e0',
              p: 1
            }}>
              {/* Navigation Buttons - Smaller */}
              <Box sx={{
                display: 'flex',
                gap: 1,
                mb: 1
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={currentFormIndex === 0}
                  onClick={() => setActiveForm(formsList[currentFormIndex - 1]?.formId)}
                  sx={{ 
                    borderColor: '#007AFF',
                    color: '#007AFF',
                    py: 0.5,
                    fontSize: '0.875rem',
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#c7c7cc'
                    }
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  disabled={currentFormIndex === formsList.length - 1}
                  onClick={() => setActiveForm(formsList[currentFormIndex + 1]?.formId)}
                  sx={{ 
                    bgcolor: '#007AFF',
                    py: 0.5,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: '#0051D5'
                    },
                    '&:disabled': {
                      bgcolor: '#e0e0e0'
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
              
              {/* Form Progress Dots - Smaller */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center'
              }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {formsList.map((form, index) => (
                    <Box
                      key={index}
                      onClick={() => setActiveForm(form.formId)}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: index === currentFormIndex 
                          ? '#007AFF' 
                          : form.isCompleted 
                            ? '#4caf50' 
                            : '#e0e0e0',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            
            {/* Form Content */}
            <Box 
              // Swipe functionality disabled - too sensitive
              // onTouchStart={onTouchStart}
              // onTouchMove={onTouchMove}
              // onTouchEnd={onTouchEnd}
              sx={{ 
                flex: 1, 
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                overscrollBehavior: 'contain', // Prevent pull-to-refresh
                height: '100%',
                position: 'relative',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)' // iOS safe area
              }}
            >
              <Box sx={{ 
                bgcolor: 'white',
                m: 2,
                mb: 10, // Extra bottom margin on mobile for Safari URL bar
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                p: 2,
                minHeight: 'calc(100% - 32px)'
              }}>
                <FormRenderer
                  activeForm={activeForm}
                  employeeId={employeeId}
                  onFormSubmit={(formId) => updateFormStatus(formId, true)}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box
      sx={{
        width: '100%',
        mx: 'auto',
        p: 2,
        display: 'flex',
        gap: 3,
        boxSizing: 'border-box',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '300px',
          flexShrink: 0,
          borderRadius: 1,
          height: 'fit-content',
          boxShadow: 2,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
            Forms
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {formsList.map((form) => (
              <ListItemButton
                key={form.formId}
                selected={activeForm === form.formId}
                onClick={() => setActiveForm(form.formId)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  width: '100%',
                }}
              >
                <ListItemText
                  primary={form.formName}
                  secondary={
                    <Chip 
                      label={form.isCompleted ? "Completed" : "Incomplete"}
                      color={form.isCompleted ? "success" : "warning"}
                      size="small"
                    />
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            ))}
          </List>

          {/* Company benefits documents live under the forms list, visually
              separated because they are reference material with no completion
              state, not another item to check off. */}
          <BenefitsResources inline />
        </Box>
      </Paper>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {activeForm ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 1,
              boxShadow: 1,
              width: '95%',
              minHeight: '600px',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              {formsList.find((f) => f.formId === activeForm)?.formName}
            </Typography>
            <FormRenderer 
              activeForm={activeForm} 
              employeeId={employeeId}
              onFormSubmit={(formId) => updateFormStatus(formId, true)}
            />
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 1,
              boxShadow: 1,
              width: '95%',
              minHeight: '600px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Select a form from the list to get started
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}